/**
 * Shared open/closed state for the Swift AI chat panel (`PMChatWidget`).
 *
 * Spec 130 F4 — "even I couldn't figure out there was an AI bot." The panel is
 * now reachable from a labelled **✨ Ask AI** button in the app header as well
 * as its floating bubble, so the open flag can no longer live inside the widget
 * that renders the bubble. It sits here, in a 20-line external store, rather
 * than a context provider: the header and the widget are cousins in the
 * `AppLayout` tree, and a provider around both would re-render every page on
 * every open/close.
 *
 * `useSyncExternalStore` (React 18) keeps the two subscribers consistent —
 * no prop drilling, no DOM-click hacks against the bubble.
 */
import { useSyncExternalStore } from 'react';

let isOpen = false;
const listeners = new Set<() => void>();

function emit() {
  for (const l of listeners) l();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

const getSnapshot = () => isOpen;

/**
 * Server snapshot. Deliberately NOT `getSnapshot` — module state is per-process
 * on a server, so returning it would bleed one request's panel state into
 * another's HTML. There is no SSR today; this keeps it safe if that changes.
 */
const getServerSnapshot = () => false;

/** Open, close, or toggle the Swift AI panel from anywhere in the app. */
export function setPmChatOpen(next: boolean | ((prev: boolean) => boolean)): void {
  const value = typeof next === 'function' ? next(isOpen) : next;
  if (value === isOpen) return;
  isOpen = value;
  emit();
}

/** Subscribe a component to the panel's open state. */
export function usePmChatOpen(): boolean {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
